import React from "react";
import { Control, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import {
  ColorController,
  Divider,
  NumberController,
  ReactSelectController,
  Text,
  TextController,
} from "@pushpress/shared-components";
import { AppointmentTypeForm } from "./AppointmentTypeDrawer.types";
import AppointmentTypeAvailability from "./AppointmentTypeAvailability";
import { StaffForAppointmentTypeFragment } from "./AppointmentTypeDrawer-generated.hooks";
import { joinStrings } from "../../../utils/misc";
import { memberStatusOptions } from "./AppointmentTypeDrawer.utils";

interface AppointmentTypeDetailsProps {
  control: Control<AppointmentTypeForm>;
  setValue: UseFormSetValue<AppointmentTypeForm>;
  trigger: UseFormTrigger<AppointmentTypeForm>;
  staff: (StaffForAppointmentTypeFragment | null)[];
}

function AppointmentTypeDetails(props: AppointmentTypeDetailsProps) {
  const { control, setValue, trigger, staff } = props;

  const getStaffOptionLabel = (option: StaffForAppointmentTypeFragment) => {
    const { userUuid, firstName, lastName } = option;

    // Some appointment types have staff that was deleted or demoted and they are returned in the appointment type staff field but not in staff query
    const isDeactivated = staff
      .filter((item) => item !== null)
      .every((item) => item.userUuid !== userUuid);
    if (isDeactivated) {
      return joinStrings(firstName, lastName, "(Deactivated)");
    }

    return joinStrings(firstName, lastName);
  };

  const getStaffOptionValue = (option: StaffForAppointmentTypeFragment) => {
    const { userUuid } = option;
    return userUuid;
  };

  const validStaff = staff.filter(
    (item) => item !== null,
  ) as StaffForAppointmentTypeFragment[];

  return (
    <div className="p-4">
      <TextController
        label="Name"
        placeholder="e.g. Personal Training"
        control={control}
        name="name"
        required
        autoComplete="off"
      />
      <ColorController
        label="Color"
        control={control}
        name="color"
        required
        autoComplete="off"
      />
      <NumberController
        label="Duration"
        control={control}
        name="duration"
        endOuterText="minutes"
        inputWidth="100px"
        required
        autoComplete="off"
        min={1}
      />
      <ReactSelectController
        control={control}
        name="memberStatus"
        label="Client status"
        required
        placeholder="— Add client status —"
        options={memberStatusOptions}
        helperText="What should be the status of clients that have this appointment type?"
        className="mb-3"
      />
      <Divider vPadding={8} marginRight={-28} />
      <Text variant="heading-5" color="wolf-800" className="mt-3" weight="600">
        Staff
      </Text>
      <Text variant="body-md" color="wolf-600">
        Set who appointments can be booked with
      </Text>
      <ReactSelectController
        label="Staff"
        control={control}
        name="staff"
        placeholder="— Add staff —"
        className="mt-4"
        isMulti
        hideLabel
        required
        hideDropdownIndicator
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        options={validStaff}
        getOptionLabel={getStaffOptionLabel}
        getOptionValue={getStaffOptionValue}
      />
      <Divider vPadding={8} marginRight={-28} />
      <Text variant="heading-5" color="wolf-800" className="mt-3" weight="600">
        General availability
      </Text>
      <Text variant="body-md" color="wolf-600" className="mb-3">
        Set when appointments should be regularly available
      </Text>
      <AppointmentTypeAvailability
        control={control}
        setValue={setValue}
        trigger={trigger}
      />
    </div>
  );
}

export default AppointmentTypeDetails;
